import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  plansApi,
  type BranchPendingPlansResponse,
  type PendingApprovalCountResponse,
  type ApprovePlanRequest,
  type ApprovePlanResponse,
  type RejectPlanRequest,
  type RejectPlanResponse,
} from '../api';
import { QUERY_KEYS } from './query-keys';

const BRANCH_PENDING_PLANS_KEY = 'branch-pending-plans';
const PENDING_APPROVAL_COUNT_KEY = 'pending-approval-count';

/**
 * Получение планов филиала для согласования
 */
export function useBranchPendingPlans(year: number, month: number) {
  return useQuery<BranchPendingPlansResponse, Error>({
    queryKey: [BRANCH_PENDING_PLANS_KEY, { year, month }],
    queryFn: () => plansApi.getBranchPendingPlans(year, month),
    refetchInterval: 30000, // Обновлять каждые 30 секунд
    staleTime: 10000, // Данные считаются свежими 10 секунд
  });
}

/**
 * Получение кол-ва планов требующих согласования (для индикатора в навигации)
 * @param enabled - включить запрос (по умолчанию true)
 */
export function usePendingApprovalCount(enabled = true) {
  return useQuery<PendingApprovalCountResponse, Error>({
    queryKey: [PENDING_APPROVAL_COUNT_KEY],
    queryFn: () => plansApi.getPendingApprovalCount(),
    enabled,
    refetchInterval: enabled ? 60000 : false, // Обновлять каждую минуту если включено
    staleTime: 30000, // Данные считаются свежими 30 секунд
  });
}

/**
 * Согласование плана (Филиал)
 */
export function useApprovePlan() {
  const queryClient = useQueryClient();

  return useMutation<ApprovePlanResponse, Error, ApprovePlanRequest>({
    mutationFn: (data) => plansApi.approvePlan(data),
    onSuccess: (_, variables) => {
      // Инвалидируем кэш планов для текущего периода
      queryClient.invalidateQueries({
        queryKey: [BRANCH_PENDING_PLANS_KEY, { year: variables.year, month: variables.month }],
      });
      // Инвалидируем счётчик ожидающих согласования
      queryClient.invalidateQueries({
        queryKey: [PENDING_APPROVAL_COUNT_KEY],
      });
      // Инвалидируем PNL - после согласования плана данные PNL должны обновиться
      queryClient.invalidateQueries({
        queryKey: ['branch-pnl'],
      });
      // Инвалидируем планы (общий кэш)
      queryClient.invalidateQueries({
        queryKey: ['plans'],
      });
      // Инвалидируем дашборд подрядчика - после согласования плана данные должны обновиться
      queryClient.invalidateQueries({
        queryKey: ['contractor-dashboard'],
      });
      queryClient.invalidateQueries({
        queryKey: ['contractor-summary'],
      });
      // Инвалидируем платежи — после согласования план появляется на странице платежей
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENTS_TM] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_INDICATORS] });
    },
  });
}

/**
 * Отклонение плана (Филиал)
 */
export function useRejectPlan() {
  const queryClient = useQueryClient();

  return useMutation<RejectPlanResponse, Error, RejectPlanRequest>({
    mutationFn: (data) => plansApi.rejectPlan(data),
    onSuccess: (_, variables) => {
      // Инвалидируем кэш планов для текущего периода
      queryClient.invalidateQueries({
        queryKey: [BRANCH_PENDING_PLANS_KEY, { year: variables.year, month: variables.month }],
      });
      // Инвалидируем счётчик ожидающих согласования
      queryClient.invalidateQueries({
        queryKey: [PENDING_APPROVAL_COUNT_KEY],
      });
      // Инвалидируем PNL - после отклонения плана данные PNL могут измениться
      queryClient.invalidateQueries({
        queryKey: ['branch-pnl'],
      });
      // Инвалидируем планы (общий кэш)
      queryClient.invalidateQueries({
        queryKey: ['plans'],
      });
      // Инвалидируем дашборд подрядчика - после отклонения плана данные должны обновиться
      queryClient.invalidateQueries({
        queryKey: ['contractor-dashboard'],
      });
      queryClient.invalidateQueries({
        queryKey: ['contractor-summary'],
      });
      // Инвалидируем платежи — после отклонения план может исчезнуть со страницы платежей
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENTS_TM] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_INDICATORS] });
    },
  });
}
